import type { FetchError } from 'ofetch'
import { defineStore } from 'pinia'

import AuthScope from '~/enums/auth-scope'
import type CooperationTypeEnum from '~/enums/cooperation-type'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { Page } from '~/types/json-api'
import type { SupplierData } from '~/types/suppliers'
import type { UserContactData } from '~/types/users'

export const messages = {
  failedToCreateSupplier: 'Не удалось создать поставщика.',
  failedToDeleteSupplier: 'Не удалось удалить поставщика.',
  failedToGetOOS: 'Не удалось получить OOS.',
  failedToGetSupplier: 'Не удалось получить поставщика.',
  failedToGetSuppliers: 'Не удалось получить поставщиков.',
  failedToUpdateSupplier: 'Не удалось сохранить поставщика.',
  nameFieldCannotBeEmpty: 'Поле «Название» не может быть пустым.',
  noSuppliers: 'Нет поставщиков.',
  noSuppliersHaveBeenFound: 'Поставщики не найдены.',
  supplierIsCreated: 'Поставщик создан.',
  supplierIsDeleted: 'Поставщик удалён.',
  supplierIsSaved: 'Поставщик сохранен.'
}

type State = {
  filter: {
    cooperationTypeId: CooperationTypeEnum | null
    query: string | null
  },
  form: any
  hasUnsavedChanges: boolean
  isSuppliersDataLoading: boolean
  isSupplierDataLoading: boolean
  isDataLoadingError: boolean
  isLoading: boolean
  percOfOSS: number | null
  page: Page
  suppliersData: SupplierData[]
  supplierData: SupplierData | null
  totalPages: number | null
  totalSuppliers: number | null
}

const initialState = (): State => ({
  filter: {
    cooperationTypeId: null,
    query: null
  },
  form: {
    actualAddress: null,
    additionalPhoneNumber: null,
    adminComment: null,
    brands: null,
    commission: null,
    cooperationTypeId: null,
    countryOfOrigin: null,
    deleteSupplierContactIds: [],
    deliveryTime: null,
    email: null,
    emailRaw: null,
    legalEntity: null,
    name: null,
    nameRaw: null,
    paymentConditions: null,
    phoneNumber: null,
    phoneNumberRaw: null,
    sellingBuying: null,
    supplierContacts: [],
    updateSupplierContacts: [],
    userIds: []
  },
  hasUnsavedChanges: false,
  isSuppliersDataLoading: false,
  isSupplierDataLoading: false,
  isDataLoadingError: false,
  isLoading: false,
  percOfOSS: null,
  page: {
    number: 1,
    size: 20
  },
  suppliersData: [],
  supplierData: null,
  totalPages: null,
  totalSuppliers: null
})

export const useSuppliersStore = defineStore('suppliers', {
  state: () => initialState(),
  getters: {
    getSupplier: (state: State) => state.supplierData,
    getSuppliers: (state: State) => state.suppliersData,
    getSupplierContacts: (state: State) => state.supplierData?.relationships.supplierContacts.data || []
  },
  actions: {
    addSupplierContact (): void {
      this.form.supplierContacts.push({
        fullName: null,
        email: null,
        phoneNumber: null,
        post: null
      })
    },
    clearFilter (): void {
      this.filter = { cooperationTypeId: null, query: null }
    },
    clearForm (): void {
      this.form.actualAddress = null
      this.form.additionalPhoneNumber = null
      this.form.adminComment = null
      this.form.brands = null
      this.form.commission = null
      this.form.cooperationTypeId = null
      this.form.countryOfOrigin = null
      this.form.deleteSupplierContactIds = []
      this.form.deliveryTime = null
      this.form.email = null
      this.form.emailRaw = null
      this.form.legalEntity = null
      this.form.name = null
      this.form.nameRaw = null
      this.form.paymentConditions = null
      this.form.phoneNumber = null
      this.form.phoneNumberRaw = null
      this.form.sellingBuying = null
      this.form.supplierContacts = []
      this.form.updateSupplierContacts = []
      this.form.userIds = []
    },
    clearSuppliers (): void {
      this.suppliersData = []
    },
    dispose (): void {
      this.clearFilter()
      this.clearSuppliers()
    },
    dropSupplierContactForId (supplierContactId: string): void {
      const index = this.form.supplierContacts.findIndex((x: any) => x.id === supplierContactId)

      this.form.supplierContacts.splice(index, 1)

      this.form.deleteSupplierContactIds.push(supplierContactId)
    },
    dropSupplierContactForIndex (supplierContactIndex: number): void {
      this.form.supplierContacts.splice(supplierContactIndex, 1)
    },
    setFormUpdatedData (): void {
      if (this.supplierData) {
        this.form.supplierContacts.filter(
          (x: any) => x.changed !== undefined && x.changed === true && x.id
        ).forEach((x: any) => {
          this.form.updateSupplierContacts.push({
            id: x.id,
            fullName: x.fullName,
            email: x.email,
            phoneNumber: x.phoneNumber,
            post: x.post
          })
        })
      }
    },
    supplierToForm (): void {
      if (this.supplierData) {
        this.clearForm()

        this.form.actualAddress = this.supplierData.attributes.actualAddress
        this.form.additionalPhoneNumber = this.supplierData.attributes.additionalPhoneNumber
        this.form.adminComment = this.supplierData.attributes.adminComment
        this.form.brands = this.supplierData.attributes.brands
        this.form.commission = this.supplierData.attributes.commission
        this.form.cooperationTypeId = this.supplierData.attributes.cooperationTypeId
        this.form.countryOfOrigin = this.supplierData.attributes.countryOfOrigin
        this.form.deliveryTime = this.supplierData.attributes.deliveryTime
        this.form.email = this.supplierData.attributes.email
        this.form.emailRaw = this.supplierData.attributes.emailRaw
        this.form.legalEntity = this.supplierData.attributes.legalEntity
        this.form.name = this.supplierData.attributes.name
        this.form.nameRaw = this.supplierData.attributes.nameRaw
        this.form.paymentConditions = this.supplierData.attributes.paymentConditions
        this.form.phoneNumber = this.supplierData.attributes.phoneNumber
        this.form.phoneNumberRaw = this.supplierData.attributes.phoneNumberRaw
        this.form.sellingBuying = this.supplierData.attributes.sellingBuying

        this.form.supplierContacts = this.supplierData.relationships.supplierContacts.data.map((x: any): any => ({
          id: x.id,
          fullName: x.attributes.fullName,
          email: x.attributes.email,
          phoneNumber: x.attributes.phoneNumber,
          post: x.attributes.post
        }))

        this.form.userIds = this.supplierData.relationships.users?.data.map((x: UserContactData) => x.id) ?? []
      }
    },
    updateSupplierContact (supplierContactId: null | string | undefined): void {
      if (supplierContactId) {
        this.form.supplierContacts.find((x: any) => x.id === supplierContactId).changed = true
      }
    },
    validate (): boolean {
      const { $toast } = useNuxtApp()

      if (!this.form.name) {
        $toast.error(messages.nameFieldCannotBeEmpty)

        return false
      }

      return true
    },
    async createSupplier (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      if (!this.validate()) {
        return
      }

      this.isLoading = true

      try {
        const { data } = await $apiHelper.suppliers.createSupplier(
          {
            actualAddress: this.form.actualAddress,
            additionalPhoneNumber: this.form.additionalPhoneNumber,
            adminComment: this.form.adminComment,
            brands: this.form.brands,
            commission: this.form.commission,
            cooperationTypeId: this.form.cooperationTypeId,
            countryOfOrigin: this.form.countryOfOrigin,
            deliveryTime: this.form.deliveryTime,
            email: this.form.email,
            emailRaw: this.form.emailRaw,
            legalEntity: this.form.legalEntity,
            name: this.form.name,
            nameRaw: this.form.nameRaw,
            paymentConditions: this.form.paymentConditions,
            phoneNumber: this.form.phoneNumber,
            phoneNumberRaw: this.form.phoneNumberRaw,
            sellingBuying: this.form.sellingBuying,
            supplierContacts: this.form.supplierContacts.filter((x: any) => !x.id).map((x: any): any => ({
              fullName: x.fullName,
              email: x.email,
              phoneNumber: x.phoneNumber,
              post: x.post
            })),
            userIds: this.form.userIds
          },
          AuthScope.Admin
        ) as any
        const response2 = await $apiHelper.suppliers.getSupplier(data.id, AuthScope.Admin)
        const { data: supplierData } = JsonApiHelper.denormalizeResponse(response2)

        this.supplierData = supplierData

        this.supplierToForm()

        $toast.success(messages.supplierIsCreated)
      } catch (error) {
        console.error(error)

        ApiErrorHandler.handleFetchError(error as FetchError, messages.failedToCreateSupplier)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    },
    async deleteSupplier (supplierId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        await $apiHelper.suppliers.deleteSupplier(supplierId, AuthScope.Admin)

        const supplierIndex = this.suppliersData.findIndex((x: SupplierData) => x.id === supplierId)
        this.suppliersData.splice(supplierIndex, 1)

        $toast.success(messages.supplierIsDeleted)
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToDeleteSupplier)
      }

      this.isLoading = false
    },
    async fetchOOS (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      try {
        const response = await $apiHelper.suppliers.getOOS() as any

        this.percOfOSS = response.meta.percOfOSS
      } catch (error) {
        console.error(error)
        $toast.error(messages.failedToGetOOS)
      }
    },
    async fetchSupplier (supplierId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isSupplierDataLoading = true
      this.isDataLoadingError = false

      try {
        const response = await $apiHelper.suppliers.getSupplier(supplierId, AuthScope.Admin)
        const { data } = JsonApiHelper.denormalizeResponse(response)

        this.supplierData = data

        this.supplierToForm()

        this.isSupplierDataLoading = false
      } catch (error) {
        this.isDataLoadingError = true

        console.error(error)

        $toast.error(messages.failedToGetSupplier)
      }
    },
    async fetchSuppliers (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isSuppliersDataLoading = true
      this.totalPages = null

      const { cooperationTypeId, query } = this.filter
      const { number, size } = this.page
      const params = helpers.getParams([
        { name: 'filter[cooperationTypeId]', value: cooperationTypeId, condition: cooperationTypeId !== null },
        { name: 'filter[query]', value: query, condition: query },
        { name: 'include', value: 'users', condition: true },
        { name: 'page[number]', value: number, condition: true },
        { name: 'page[size]', value: size, condition: true }
      ])

      try {
        const response = await $apiHelper.suppliers.getSuppliers(params, AuthScope.Admin)
        const { data, meta } = JsonApiHelper.denormalizeResponse(response)

        this.totalSuppliers = meta.totalSuppliers
        this.totalPages = helpers.getTotalPages(meta.totalSuppliers, this.page.size)

        this.suppliersData = data

        this.isSuppliersDataLoading = false
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToGetSuppliers)
      }
    },
    async updateSupplier (supplierId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      if (!this.validate()) {
        return
      }

      this.isLoading = true

      this.setFormUpdatedData()

      try {
        await $apiHelper.suppliers.updateSupplier(
          supplierId,
          {
            actualAddress: this.form.actualAddress,
            additionalPhoneNumber: this.form.additionalPhoneNumber,
            adminComment: this.form.adminComment,
            brands: this.form.brands,
            commission: this.form.commission,
            cooperationTypeId: this.form.cooperationTypeId,
            countryOfOrigin: this.form.countryOfOrigin,
            deleteSupplierContactIds: this.form.deleteSupplierContactIds,
            deliveryTime: this.form.deliveryTime,
            email: this.form.email,
            emailRaw: this.form.emailRaw,
            legalEntity: this.form.legalEntity,
            name: this.form.name,
            nameRaw: this.form.nameRaw,
            paymentConditions: this.form.paymentConditions,
            phoneNumber: this.form.phoneNumber,
            phoneNumberRaw: this.form.phoneNumberRaw,
            sellingBuying: this.form.sellingBuying,
            supplierContacts: this.form.supplierContacts.filter((x: any) => !x.id).map((x: any): any => ({
              fullName: x.fullName,
              email: x.email,
              phoneNumber: x.phoneNumber,
              post: x.post
            })),
            updateSupplierContacts: this.form.updateSupplierContacts,
            userIds: this.form.userIds
          },
          AuthScope.Admin
        )
        const response2 = await $apiHelper.suppliers.getSupplier(supplierId, AuthScope.Admin)
        const { data: supplierData } = JsonApiHelper.denormalizeResponse(response2)

        this.supplierData = supplierData

        $toast.success(messages.supplierIsSaved)

        this.supplierToForm()
      } catch (error) {
        console.error(error)

        ApiErrorHandler.handleFetchError(error as FetchError, messages.failedToUpdateSupplier)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    }
  }
})
